import config from '../../config/config';
import DefaultApiLimit from '../../config/defaultLimit';

import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

/**
 * Query parameters interface for location management API requests.
 */
interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

/**
 * Location management API service providing endpoints for charging locations,
 * devices, connectors, maintenance schedules, reports, and activity logs.
 */
const locationManagementApi = {
  /**
   * Fetches the charging point list without pagination.
   * @param {any} data - The query parameters or filter payload.
   * @returns {Promise<any>} A promise that resolves with the charging points.
   */
  fetchChargingPointWithoutPagination(data: any): Promise<any> {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point`, { ...data });
  },

  /**
   * Fetches average uptime and downtime statistics for charging locations.
   * @param {any} data - The query parameters or filter payload.
   * @returns {Promise<any>} A promise that resolves with the uptime statistics.
   */
  fetchAvgOnlineOffline(data: any): Promise<any> {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/avg-uptime-downtime`,
      { ...data },
    );
  },

  /**
   * Fetches the list of all charging station devices.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the device list.
   */
  fetchDeviceList(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/device-list`, {
      ...parameters,
    });
  },

  /**
   * Fetches the list of location activity logs (OCPP logs).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the activity list.
   */
  fetchLocationActivityList(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/ocpp-logs`, {
      ...parameters,
    });
  },

  /**
   * Fetch station activity list (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the activity list.
   */
  fetchStationActivityList(query: QueryParameters): Promise<any> {
    return this.fetchLocationActivityList(query);
  },

  /**
   * Exports location activity logs based on the provided query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the export data.
   */
  fetchLocationActivityExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/ocpp-logs-export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch station activity export (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the export data.
   */
  fetchStationActivityExport(query: QueryParameters): Promise<any> {
    return this.fetchLocationActivityExport(query);
  },

  /**
   * Fetches available filter options for the location activity list.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the activity list filters.
   */
  fetchLocationActivityListFilters(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/ocpp-logs/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch station activity list filters (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the list filters.
   */
  fetchStationActivityListFilters(query: QueryParameters): Promise<any> {
    return this.fetchLocationActivityListFilters(query);
  },

  /**
   * Fetches location chart data for time-based transaction report.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the chart data.
   */
  fetchLocationChartData(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/time-based-transaction-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch station chart data (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the chart data.
   */
  fetchStationChartData(query: QueryParameters): Promise<any> {
    return this.fetchLocationChartData(query);
  },

  /**
   * Fetches detailed information for a specific location.
   * @param {string} id - The location ID.
   * @param {QueryParameters} [query] - Optional query parameters.
   * @returns {Promise<any>} A promise that resolves with the location details.
   */
  fetchLocationDetails(id: string, query?: QueryParameters): Promise<any> {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/${id}`, query);
  },

  /**
   * Fetch station details (backward compatibility alias).
   * @param {string} id - The station ID.
   * @param {QueryParameters} [query] - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the station details.
   */
  fetchStationDetails(id: string, query?: QueryParameters): Promise<any> {
    return this.fetchLocationDetails(id, query);
  },

  /**
   * Fetches connector status information based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with connector status.
   */
  fetchConnectorStatus(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/connector-status`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the list of charging transactions.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the transactions.
   */
  fetchTransactionList(query: QueryParameters): Promise<any> {
    const sn = query?.chargingPointSn || query?.chargingpointsn;
    const accountCustomIdValue = query?.accountCustomId;
    const station = query?.stationId || query?.chargingStation || query?.locationId;

    const parameters: Record<string, any> = {
      ...query,
      limit: query?.limit || DefaultApiLimit.limit,
    };
    delete parameters.chargingpointsn;
    delete parameters.chargingPointSn;
    delete parameters.accountCustomId;
    delete parameters.chargingStation;
    delete parameters.stationId;
    delete parameters.locationId;

    if (sn) {
      parameters.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      parameters.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      parameters.stationId = Array.isArray(station) ? station : [station];
    }

    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the available filters for the transaction list.
   * @returns {Promise<any>} A promise that resolves with the transaction list filter data.
   */
  fetchTransactionListFilter(): Promise<any> {
    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list/filter-list`,
    );
  },

  /**
   * Exports the transaction list based on the provided query parameters.
   * @param {QueryParameters} query - The query parameters used to filter the transactions for export.
   * @returns {Promise<any>} A promise that resolves with the exported transaction data.
   */
  fetchTransactionListExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-transaction/export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches transaction chart data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the transaction chart data.
   */
  fetchTransactionChart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/power-consumption-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the guest charging list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the guest charging list.
   */
  fetchGuestChargingList(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/free-charging-schedules`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches guest charging chart data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the guest charging chart data.
   */
  fetchGuestChargingchart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/schedule-charging-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches available filters for the guest charging list.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the guest charging filters.
   */
  fetchGuestChargingListFilter(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/free-charging-schedules/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Exports the guest charging list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported guest charging data.
   */
  fetchGuestChargingListExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/free-charging-schedules/export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches maintenance schedules based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the maintenance schedule list.
   */
  fetchMaintenanceSchedule(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/maintenance-schedule`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Exports maintenance schedule records based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported maintenance schedule data.
   */
  fetchMaintenanceScheduleExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/maintenance-schedule/export-data`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches available filter options for maintenance schedules.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the maintenance schedule filters.
   */
  fetchMaintenanceScheduleFilters(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/maintenance-schedule-report/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches maintenance calendar data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the maintenance calendar data.
   */
  fetchMaintenanceCalendars(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/maintenance-schedule-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the report summary overview for charging points.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the report summary.
   */
  fetchReportSummary(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-point-report-overview`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Exports report summary data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported report summary data.
   */
  fetchReportSummaryExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-point-report-overview/export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches available filter options for the report summary overview.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the report summary filters.
   */
  fetchReportSummaryFilters(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-point-report-overview/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches power consumed metrics for charging locations.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the power consumed report data.
   */
  fetchReportPowerConsumed(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-power-consumption`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches revenue chart metrics based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the revenue chart data.
   */
  fetchRevenueChart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/power-consumption-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches utilization and efficiency metrics based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the utilization report.
   */
  fetchReportUtilization(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-utilization`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Exports utilization and efficiency metrics based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported utilization report.
   */
  fetchReportUtilizationExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-utilization/export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches available filter options for the utilization and efficiency report.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the utilization report filters.
   */
  fetchReportUtilizationFilters(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-utilization/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches charging locations list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the charging locations list.
   */
  fetchChargingLocationList(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-list`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch charging station list (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the list.
   */
  fetchChargingStationList(query: QueryParameters): Promise<any> {
    return this.fetchChargingLocationList(query);
  },

  /**
   * Fetches charging station devices based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the devices list.
   */
  fetchDevicesList(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-point-list`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Adds a lane map for a specific charging location.
   * @param {string | number} chargingLocationId - The charging location ID.
   * @param {any} data - The lane map data payload or FormData.
   * @returns {Promise<any>} A promise that resolves with the created lane map.
   */
  addLaneMap(chargingLocationId: string | number, data: any): Promise<any> {
    return postRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station-map/${chargingLocationId}`,
      data,
      true,
    );
  },

  /**
   * Updates an existing lane map for a charging location.
   * @param {number | string} chargingLocationId - The charging location ID.
   * @param {number | string} laneMapId - The lane map ID to update.
   * @param {any} data - The updated lane map data payload or FormData.
   * @returns {Promise<any>} A promise that resolves with the updated lane map.
   */
  editLaneMap(
    chargingLocationId: number | string,
    laneMapId: number | string,
    data: any,
  ): Promise<any> {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station-map/${chargingLocationId}/${laneMapId}`,
      data,
      true,
    );
  },

  /**
   * Fetches the lane map list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the lane map list.
   */
  fetchLaneMapList(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station-map`, {
      ...parameters,
    });
  },

  /**
   * Fetches the historical faults list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the historical faults list.
   */
  fetchHistoricalFaultsList(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/historical-fault-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches available filter options for the historical faults list.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the historical faults list filters.
   */
  fetchHistoricalFaultsListFilter(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/historical-fault-report/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Exports historical fault records based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported historical faults data.
   */
  fetchHistoricalFaultsListExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/historical-fault-report/export`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches hourly heatmap metrics for charging stations.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the hourly heatmap data.
   */
  fetchHeatMapByHour(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/transaction-duration-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches session count per station/location chart data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the session count chart data.
   */
  fetchSessionCountPerLocationChart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-transaction/session-count-per-station`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch session count per station chart (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the chart data.
   */
  fetchSessionCountPerStationChart(query: QueryParameters): Promise<any> {
    return this.fetchSessionCountPerLocationChart(query);
  },

  /**
   * Fetches comparison report data for energy consumption and revenue.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the comparison report data.
   */
  fetchEnergyRevenueComparison(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-comparison-report`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches heatmap chart data for charging stations.
   * @returns {Promise<any>} A promise that resolves with the heatmap chart data.
   */
  fetchHeatMapChartData(): Promise<any> {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/transaction-duration-report`,
    );
  },

  /**
   * Fetches the alerts list based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the alerts list.
   */
  fetchAlertsList(query: QueryParameters): Promise<any> {
    const parameters = { ...query, limit: query?.limit || DefaultApiLimit.limit };
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/alerts`, {
      ...parameters,
    });
  },

  /**
   * Exports alerts records based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the exported alerts data.
   */
  fetchAlertsExport(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/alerts-export`, {
      ...parameters,
    });
  },

  /**
   * Fetches available filter options for the alerts list.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the alerts list filters.
   */
  fetchAlertsListFilters(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/alerts/list-filters`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches alerts chart metrics based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the alerts chart data.
   */
  fetchAlertsChart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-station/alerts-report`, {
      ...parameters,
    });
  },

  /**
   * Fetches alerts point information based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the alerts point information.
   */
  fetchAlertsPointInfo(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/alerts-charging-point-info`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches uptime chart data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the uptime chart data.
   */
  fetchUptimeChartData(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/uptime-by-station`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches uptime trend metrics over time based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the uptime trend data.
   */
  fetchUptimeOverTimeChartData(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/uptime-trend-over-time`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the number of schedules based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the number of schedules.
   */
  fetchNoOfSchedules(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/station-schedule-hours`,
      { ...parameters },
    );
  },

  /**
   * Fetches uptime list and chart data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the uptime list and chart data.
   */
  fetchUptimeListAndChart(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-consumption-month-wise`,
      { ...parameters },
    );
  },

  /**
   * Fetches today's uptime data based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with today's uptime data.
   */
  fetUpTimeTodayData(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/charging-station-consumption-date-wise`,
      { ...parameters },
    );
  },

  /**
   * Updates guest charging status for a specific record.
   * @param {string} id - The guest charging record ID.
   * @param {any} data - The status update payload.
   * @returns {Promise<any>} A promise that resolves with the update response.
   */
  updateGuestStatus(id: string, data: any): Promise<any> {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/free-charging/updateActiveStatus/${id}`,
      data,
      false,
    );
  },

  /**
   * Fetches vehicle information based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the vehicle information.
   */
  fetchVehicleInformation(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/facility/v1/charging-station/get-vehicle-information`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches device connector status based on query parameters.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the device connector status.
   */
  getDeviceConnectorStatus(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/device-connector-status-count`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetches the count of device statuses for charging locations.
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the device status count.
   */
  getLocationDeviceStatusCount(query: QueryParameters): Promise<any> {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/station-device-status-count`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Get station device status count (backward compatibility alias).
   * @param {QueryParameters} query - The query parameters.
   * @returns {Promise<any>} A promise that resolves with the count.
   */
  getStationDeviceStatusCount(query: QueryParameters): Promise<any> {
    return this.getLocationDeviceStatusCount(query);
  },
};

export default locationManagementApi;
